import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { StatusField } from '../project-update-fields/status';
import { TargetDateField } from '../project-update-fields/target-date';

import type { projectStatusUpdateCardHeader_AtlassianConversationAssistantMessageActions_CardHeader$key } from './__generated__/projectStatusUpdateCardHeader_AtlassianConversationAssistantMessageActions_CardHeader.graphql';
import { messages } from './messages';

type Payload = ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate];

const styles = cssMap({
	container: {
		display: 'inline-flex',
		justifyContent: 'center',
	},
	topRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	bottomRow: {
		display: 'inline-flex',
		paddingTop: token('space.050'),
	},
	projectKey: {
		display: 'flex',
		alignItems: 'center',
	},
	projectStatus: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
	},
	projectName: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
	},
});

export type CardHeaderProps = {
	action: ActionWith<Payload>;
	fragmentKey?: projectStatusUpdateCardHeader_AtlassianConversationAssistantMessageActions_CardHeader$key | null;
};

export const CardHeader = ({ action, fragmentKey }: CardHeaderProps): React.JSX.Element | null => {
	const { phase, status, targetDate } = action.data || {};
	const projectDetails = useFragment(
		graphql`
			fragment projectStatusUpdateCardHeader_AtlassianConversationAssistantMessageActions_CardHeader on TownsquareProject {
				name
				key
				iconUrl {
					square {
						light
						dark
					}
				}
				state {
					value
				}
				dueDate {
					label
				}
			}
		`,
		fragmentKey,
	);

	const {
		name: projectName,
		key: projectKey,
		iconUrl,
		state,
		dueDate: fallbackDate,
	} = projectDetails || {};
	const intl = useIntl();

	const projectStatus = useMemo(
		() => (phase && phase !== 'in_progress' ? phase : status || state?.value),
		[phase, status, state],
	);

	const statusLozengeText = useMemo(() => {
		switch (projectStatus) {
			case 'paused':
				return intl.formatMessage(messages.phasePaused);
			case 'pending':
				return intl.formatMessage(messages.phasePending);
			case 'done':
				return intl.formatMessage(messages.phaseDone);
			case 'on_track':
				return intl.formatMessage(messages.statusOnTrack);
			case 'at_risk':
				return intl.formatMessage(messages.statusAtRisk);
			case 'off_track':
				return intl.formatMessage(messages.statusOffTrack);
			case 'cancelled':
				return intl.formatMessage(messages.phaseCancelled);
			default:
				return undefined;
		}
	}, [projectStatus, intl]);

	const statusLozengeAppearance = useMemo(() => {
		switch (projectStatus) {
			case 'on_track':
				return 'success' as const;
			case 'at_risk':
				return 'warning' as const;
			case 'off_track':
				return 'danger' as const;
			default:
				return 'neutral' as const;
		}
	}, [projectStatus]);

	const statusTextProps = {
		status: (
			<StatusField
				statusLozengeText={statusLozengeText}
				statusLozengeAppearance={statusLozengeAppearance}
			/>
		),
		date: <TargetDateField targetDate={targetDate} fallbackDate={fallbackDate?.label} />,
	};

	if (
		!projectKey &&
		!statusLozengeText &&
		!targetDate?.date &&
		!fallbackDate?.label &&
		!projectName
	) {
		return null;
	}

	return (
		<Stack xcss={styles.container}>
			<Inline xcss={styles.topRow}>
				<Box xcss={styles.projectKey}>
					{projectKey && <Text color="color.text.subtle">{projectKey}</Text>}
				</Box>
				{statusLozengeText && (targetDate?.date || fallbackDate?.label) ? (
					<Text color="color.text.subtle" size="small">
						<Inline xcss={styles.projectStatus}>
							{phase === 'done'
								? intl.formatMessage(messages.statusOnDate, statusTextProps)
								: intl.formatMessage(messages.statusForDate, statusTextProps)}
						</Inline>
					</Text>
				) : (
					<Inline xcss={styles.projectStatus}>
						<StatusField
							statusLozengeText={statusLozengeText}
							statusLozengeAppearance={statusLozengeAppearance}
						/>
						<TargetDateField targetDate={targetDate} fallbackDate={fallbackDate?.label} />
					</Inline>
				)}
			</Inline>
			{projectName && (
				<Inline xcss={styles.bottomRow}>
					<Inline xcss={styles.projectName}>
						{iconUrl?.square && (
							<Image
								src={iconUrl.square?.light ?? ''}
								srcDark={iconUrl.square?.dark ?? ''}
								alt=""
								width={20}
								height={20}
							/>
						)}
						<Text weight="bold">{projectName}</Text>
					</Inline>
				</Inline>
			)}
		</Stack>
	);
};
